"use client"
import { useEffect, useRef, useState } from 'react'
import { ChooseYourPathArrows, type ChooseYourPathArrow } from './ChooseYourPathArrows'
import {
  type ChooseYourPathDiagramConfig,
  type DiagramAnchor,
  type DiagramArrowSpec,
  type DiagramArrowStyle,
} from './chooseYourPathDiagramModel'
import { PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR } from './planPrerenderWarmupScan'

type Heights = Record<string, number>

// Horizontal extent of a box, as percentages of the diagram width.
type Span = { lo: number; hi: number }

// Measured plan box, as percentages of the diagram width.
type PlanBox = { center: number; lo: number; hi: number }

type Metrics = { width: number; boxes: PlanBox[] }

const round2 = (n: number) => Math.round(n * 100) / 100

const heightsEqual = (a: Heights, b: Heights) => {
  const keys = Object.keys(b)
  if (Object.keys(a).length !== keys.length) return false
  return keys.every(k => a[k] === b[k])
}

const metricsEqual = (a: Metrics | null, b: Metrics) => {
  if (!a || a.width !== b.width || a.boxes.length !== b.boxes.length) return false
  return a.boxes.every((box, i) =>
    box.center === b.boxes[i].center && box.lo === b.boxes[i].lo && box.hi === b.boxes[i].hi,
  )
}

// The plan-box row is a separate sibling component following
// this diagram in the DOM; tagged with data-branch-choice-ids.
const findPlanGrid = (container: HTMLElement): HTMLElement | null => {
  const grids = Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-branch-choice-ids]${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`,
    ),
  ).filter(g => g.children.length > 0)
  if (!grids.length) return null
  const following = grids.filter(
    g =>
      !!(container.compareDocumentPosition(g) & Node.DOCUMENT_POSITION_FOLLOWING),
  )
  return following[0] ?? grids[0]
}

// Reads the real plan-box row from the DOM so terminal arrows
// line up with each box however the (separate) plan grid lays
// out. Values rounded so sub-pixel jitter can't defeat the
// equality guard and spin the measure -> setState -> resize loop.
const measureMetrics = (container: HTMLElement): Metrics | null => {
  const grid = findPlanGrid(container)
  if (!grid) return null
  const containerRect = container.getBoundingClientRect()
  if (containerRect.width <= 0) return null
  const toPct = (px: number) => (px / containerRect.width) * 100
  const boxes = Array.from(grid.children).map(child => {
    const rect = (child as HTMLElement).getBoundingClientRect()
    // A grid child may pad itself to extend its hover target into the gutter
    // between plans (see DesktopPlaybookBranchChoicesFive); arrows must aim
    // at the visible box, so measure the content box, not the padded rect.
    const cs = getComputedStyle(child)
    const padLeft = parseFloat(cs.paddingLeft) || 0
    const padRight = parseFloat(cs.paddingRight) || 0
    const left = rect.left + padLeft - containerRect.left
    const width = rect.width - padLeft - padRight
    return {
      center: round2(toPct(left + width / 2)),
      lo: round2(toPct(left)),
      hi: round2(toPct(left + width)),
    }
  })
  if (!boxes.length) return null
  return { width: Math.round(containerRect.width), boxes }
}

const intersect = (a: Span, b: Span): Span | null => {
  const lo = Math.max(a.lo, b.lo)
  const hi = Math.min(a.hi, b.hi)
  return lo <= hi ? { lo, hi } : null
}

const clamp = (value: number, lo: number, hi: number) =>
  lo > hi ? (lo + hi) / 2 : Math.min(Math.max(value, lo), hi)

// Stock clearance between line ends and box edges; designs override it via
// config.arrowClearancePx (0 = flush).
const ARROW_CLEARANCE_PX = 5

const offsetFromY = (anchor: DiagramAnchor, y: number, clearance: number) =>
  anchor.edge === 'bottom' ? y + clearance : y

const offsetToY = (anchor: DiagramAnchor, y: number, clearance: number) =>
  anchor.edge === 'top' ? y - clearance : y

// Largest inset (%) we'll pull an arrow off a box edge so its
// head still reads as "inside"; shrinks for narrow overlaps.
const insetFor = (span: Span) => Math.min(2, (span.hi - span.lo) / 4)

export const ChooseYourPathDiagram = ({
  config,
  hoveredPlanIndex = null,
}: {
  config: ChooseYourPathDiagramConfig
  /** Plan-box index under the pointer; drives config.hoverEmphasis. */
  hoveredPlanIndex?: number | null
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const bubbleRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [heights, setHeights] = useState<Heights>({})
  const [metrics, setMetrics] = useState<Metrics | null>(null)

  useEffect(() => {
    let frame = 0

    const measure = () => {
      const nextHeights: Heights = {}
      for (const bubble of config.bubbles) {
        const el = bubbleRefs.current[bubble.id]
        // Round so sub-pixel reflow can't defeat the equality
        // guard and spin the measure -> setState -> resize loop.
        if (el) {
          let h = el.getBoundingClientRect().height
          // Under equalRowHeights the box is STRETCHED to the row max, so
          // measuring it would ratchet: heights could only ever grow (the
          // bloated-boxes bug after window resizes). Measure the natural
          // content height instead: text child + the box's padding/border.
          if (config.equalRowHeights && el.firstElementChild) {
            const cs = getComputedStyle(el)
            h =
              el.firstElementChild.getBoundingClientRect().height +
              parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) +
              parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth)
          }
          // Ceil, not round: under equalRowHeights the box renders AT this
          // value (minHeight), so it must not undershoot the fractional
          // natural height — rounding down leaves the box at its fractional
          // natural size, which blurs the bottom border off the pixel grid.
          nextHeights[bubble.id] = Math.ceil(h)
        }
      }
      if (Object.keys(nextHeights).length === config.bubbles.length) {
        setHeights(prev => (heightsEqual(prev, nextHeights) ? prev : nextHeights))
      }
      const container = containerRef.current
      if (container) {
        const next = measureMetrics(container)
        if (next) setMetrics(prev => (metricsEqual(prev, next) ? prev : next))
      }
    }

    const scheduleMeasure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    measure()
    // Observe only the separate plan grid: its width tracks the
    // responsive column (catching text reflow / plan-center
    // changes) but is independent of our container height. We do
    // NOT observe our own container — its height derives from this
    // state, so observing it would loop. window resize backs this
    // up for any width change that doesn't resize the grid.
    const observer = new ResizeObserver(scheduleMeasure)
    const container = containerRef.current
    if (container) {
      const grid = findPlanGrid(container)
      if (grid) observer.observe(grid)
    }
    window.addEventListener('resize', scheduleMeasure)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [config])

  const rowCount = Math.max(...config.bubbles.map(b => b.row)) + 1

  const rowHeight = (row: number): number => {
    const inRow = config.bubbles.filter(b => b.row === row)
    const measured = inRow.map(b => heights[b.id]).filter((h): h is number => h != null)
    if (measured.length === inRow.length && measured.length > 0) {
      return Math.max(...measured)
    }
    return config.estimatedRowHeights[row]
  }

  const rowTop = (row: number): number => {
    let y = config.topPad
    for (let k = 0; k < row; k++) y += rowHeight(k) + config.rowGaps[k]
    return y
  }

  const rowCenter = (row: number): number => rowTop(row) + rowHeight(row) / 2

  const totalHeight =
    rowTop(rowCount - 1) + rowHeight(rowCount - 1) + config.rowGaps[rowCount - 1]
  const arrowClearance = config.arrowClearancePx ?? ARROW_CLEARANCE_PX
  const terminalY = totalHeight - config.terminalGap - arrowClearance

  const bubbleById = (id: string) => config.bubbles.find(b => b.id === id)!

  // Under equalRowHeights every box RENDERS at the row max (minHeight), so
  // edges must anchor to that, not the natural measured height — else
  // arrows at the shorter box end inside its stretched border.
  const bubbleBoxHeight = (bubble: { id: string; row: number }) =>
    config.equalRowHeights
      ? rowHeight(bubble.row)
      : heights[bubble.id] ?? rowHeight(bubble.row)

  // Whole-pixel box top, shared by the box render and the arrow anchors:
  // fractional tops blur the 1px borders and leave hairline gaps where lines
  // are meant to sit flush against the box edges.
  const bubbleTopY = (bubble: { id: string; row: number }) =>
    Math.round(rowCenter(bubble.row) - bubbleBoxHeight(bubble) / 2)

  const anchorY = (anchor: DiagramAnchor): number => {
    const bubble = bubbleById(anchor.bubbleId)
    const top = bubbleTopY(bubble)
    return anchor.edge === 'top' ? top : top + bubbleBoxHeight(bubble)
  }

  // Bubble's horizontal extent in %. A 'right'-anchored bubble pins
  // its right edge at xPct (span hangs left of xPct); 'left' pins its
  // left edge (span hangs right); 'center' straddles xPct.
  const bubbleSpan = (bubbleId: string): Span => {
    const bubble = bubbleById(bubbleId)
    const { widthPct } = bubble
    if (bubble.anchor === 'right') {
      return { lo: bubble.xPct - widthPct, hi: bubble.xPct }
    }
    if (bubble.anchor === 'left') {
      return { lo: bubble.xPct, hi: bubble.xPct + widthPct }
    }
    return { lo: bubble.xPct - widthPct / 2, hi: bubble.xPct + widthPct / 2 }
  }

  const planBox = (index: number): PlanBox =>
    metrics?.boxes[index] ?? {
      center: ((index + 0.5) / 5) * 100,
      lo: ((index + 0.5) / 5) * 100 - 8,
      hi: ((index + 0.5) / 5) * 100 + 8,
    }

  // Re-stroke an arrow while its plan is hovered: emphasis color, own width
  // plus the configured delta. No-op unless the design opts into
  // hoverEmphasis.
  const emphasize = (base: DiagramArrowStyle | undefined, active: boolean): DiagramArrowStyle | undefined => {
    if (!active || !config.hoverEmphasis) return base
    const baseWidth = base?.strokeWidth ?? config.arrowStyle?.strokeWidth ?? 2.25
    return {
      ...base,
      color: config.hoverEmphasis.color,
      strokeWidth: baseWidth + config.hoverEmphasis.strokeWidthDelta,
    }
  }
  const specHovered = (spec: DiagramArrowSpec) =>
    hoveredPlanIndex != null && (spec.hoverPlanIndices?.includes(hoveredPlanIndex) ?? false)

  const arrows: ChooseYourPathArrow[] = []

  // Default to a straight vertical line wherever source and target
  // boxes overlap horizontally; only bend when a drop can't reach.
  for (const spec of config.arrows) {
    if (spec.kind === 'link') {
      const src = bubbleSpan(spec.from.bubbleId)
      const dst = bubbleSpan(spec.to.bubbleId)
      const fromY = offsetFromY(spec.from, anchorY(spec.from), arrowClearance)
      const toY = offsetToY(spec.to, anchorY(spec.to), arrowClearance)
      const overlap = intersect(src, dst)
      if (overlap) {
        const inset = insetFor(overlap)
        const x = clamp(spec.from.xPct, overlap.lo + inset, overlap.hi - inset)
        arrows.push({
          from: { x, y: fromY },
          to: { x, y: toY },
          label: spec.label,
          labelAnchor: spec.labelAnchor,
          labelWidthPct: spec.labelWidthPct,
          stroke: emphasize(spec.stroke, specHovered(spec)),
        })
      } else {
        // No vertical lines up: drop out of the source, then over.
        const exitX = clamp(spec.from.xPct, src.lo, src.hi)
        arrows.push({
          from: { x: exitX, y: fromY },
          to: { x: clamp(spec.to.xPct, dst.lo, dst.hi), y: toY },
          bend: 'hv',
          label: spec.label,
          labelAnchor: spec.labelAnchor,
          labelWidthPct: spec.labelWidthPct,
          stroke: emphasize(spec.stroke, specHovered(spec)),
        })
      }
    } else if (spec.kind === 'terminal') {
      const src = bubbleSpan(spec.from.bubbleId)
      const box = planBox(spec.planIndex)
      const fromY = offsetFromY(spec.from, anchorY(spec.from), arrowClearance)
      const boxInset = insetFor({ lo: box.lo, hi: box.hi })
      // Prefer a single straight vertical: pick one x that exits the source
      // box AND lands inside the plan box. When they overlap horizontally we
      // move the START to that shared x rather than dropping at the source's
      // own xPct and bending across — a kinked redirect reads worse than a
      // straight line that exits the box a little off-center.
      const reach = intersect(src, { lo: box.lo + boxInset, hi: box.hi - boxInset })
      if (reach) {
        const x = clamp(spec.from.xPct, reach.lo, reach.hi)
        arrows.push({
          from: { x, y: fromY },
          to: { x, y: terminalY },
          label: spec.label,
          labelAnchor: spec.labelAnchor,
          labelWidthPct: spec.labelWidthPct,
          stroke: emphasize(spec.stroke, specHovered(spec)),
        })
      } else {
        // No straight vertical connects them: drop out of the source, then
        // bend over to the box.
        const exitX = clamp(spec.from.xPct, src.lo, src.hi)
        const branchY = Math.min(fromY + config.branchStemLength, terminalY - 2)
        arrows.push({
          from: { x: exitX, y: fromY },
          to: { x: exitX, y: branchY },
          label: spec.label,
          labelAnchor: spec.labelAnchor,
          labelWidthPct: spec.labelWidthPct,
          // The stem is short; anchor the label in the middle of the open gap
          // it drops through (between the bubble bottom and the plan row),
          // where there's room beside the line.
          labelAt: { xPct: exitX, y: (fromY + terminalY) / 2 },
          noHead: true,
          stroke: emphasize(spec.stroke, specHovered(spec)),
        })
        arrows.push({
          from: { x: exitX, y: branchY },
          to: { x: box.center, y: terminalY },
          bend: 'hv',
          stroke: emphasize(spec.stroke, specHovered(spec)),
        })
      }
    } else {
      // One labelled stem fanning out to two plan boxes. Drop at
      // from.xPct when it can exit the source (and reach the first
      // plan box horizontally); legs that can't line up bend at the
      // fan-out crossline — same xPct rules as terminal arrows.
      const src = bubbleSpan(spec.from.bubbleId)
      const fromY = offsetFromY(spec.from, anchorY(spec.from), arrowClearance)
      const firstBox = planBox(spec.planIndices[0])
      const firstOverlap = intersect(src, { lo: firstBox.lo, hi: firstBox.hi })
      const stemX = firstOverlap
        ? clamp(
            spec.from.xPct,
            firstOverlap.lo + insetFor(firstOverlap),
            firstOverlap.hi - insetFor(firstOverlap),
          )
        : clamp(spec.from.xPct, src.lo, src.hi)
      const branchY = terminalY - config.branchCrosslineGap
      arrows.push({
        from: { x: stemX, y: fromY },
        to: { x: stemX, y: branchY },
        label: spec.label,
        labelAnchor: spec.labelAnchor,
        labelWidthPct: spec.labelWidthPct,
        // Label sits in the long stem above the fan-out crossline.
        labelAt: { xPct: stemX, y: (fromY + branchY) / 2 },
        noHead: true,
        stroke: emphasize(spec.stroke, specHovered(spec)),
      })
      for (const planIndex of spec.planIndices) {
        const box = planBox(planIndex)
        const legStroke = emphasize(
          spec.legStrokes?.[planIndex] ?? spec.stroke,
          specHovered(spec) || hoveredPlanIndex === planIndex,
        )
        if (stemX >= box.lo && stemX <= box.hi) {
          // Straight vertical continuation lands inside this box.
          arrows.push({
            from: { x: stemX, y: branchY },
            to: { x: stemX, y: terminalY },
            stroke: legStroke,
          })
        } else {
          arrows.push({
            from: { x: stemX, y: branchY },
            to: { x: box.center, y: terminalY },
            bend: 'hv',
            stroke: legStroke,
          })
        }
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: totalHeight }}
    >
      <ChooseYourPathArrows
        arrows={arrows}
        height={totalHeight}
        labelClassName={config.arrowLabelClassName}
        stroke={config.arrowStyle}
      />

      {config.bubbles.map(bubble => {
        const rightAnchored = bubble.anchor === 'right'
        const leftAnchored = bubble.anchor === 'left'
        // Once the container width is measured, position and size the box in
        // WHOLE pixels: %-derived fractional positions blur the 1px borders
        // (and the translateY(-50%) centering lands tops on half-pixels).
        // The rounded top comes from bubbleTopY, the same value the arrow
        // anchors use, so flush lines meet the border exactly.
        const w = metrics?.width
        const inset = bubble.widthInsetPx ?? 0
        const widthPx = w != null ? Math.round((bubble.widthPct / 100) * w) - inset : null
        const leftPx =
          w == null || widthPx == null
            ? null
            : rightAnchored
              ? Math.round((bubble.xPct / 100) * w) - widthPx
              : leftAnchored
                ? Math.round((bubble.xPct / 100) * w)
                : Math.round((bubble.xPct / 100) * w - widthPx / 2)
        const snapped = leftPx != null
        return (
          <div
            key={bubble.id}
            ref={el => {
              bubbleRefs.current[bubble.id] = el
            }}
            className={config.bubbleClassName}
            // Pre-measure fallback: a right-anchored bubble pins its RIGHT
            // edge at xPct (via `right`), a left-anchored one its LEFT edge,
            // so both keep the shared vertical centering but drop the
            // horizontal -translate-x-1/2 from bubbleClassName. An inline
            // transform reliably overrides both class translates.
            style={{
              ...(snapped
                ? { left: `${leftPx}px`, width: `${widthPx}px`, top: `${bubbleTopY(bubble)}px`, transform: 'none' }
                : {
                    ...(rightAnchored
                      ? { right: `${100 - bubble.xPct}%`, transform: 'translateY(-50%)' }
                      : leftAnchored
                        ? { left: `${bubble.xPct}%`, transform: 'translateY(-50%)' }
                        : { left: `${bubble.xPct}%` }),
                    top: `${rowCenter(bubble.row)}px`,
                    width: inset ? `calc(${bubble.widthPct}% - ${inset}px)` : `${bubble.widthPct}%`,
                  }),
              // equalRowHeights: stretch to the row's tallest box (stable:
              // re-measuring a stretched box returns the same max) with the
              // text vertically centered.
              ...(config.equalRowHeights
                ? { minHeight: `${rowHeight(bubble.row)}px`, display: 'flex', alignItems: 'center' }
                : {}),
            }}
          >
            <div className={bubble.textClassName ?? 'text-sm leading-snug'}>{bubble.text}</div>
          </div>
        )
      })}
    </div>
  )
}
